import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { SITE_TYPES } from "../utils/siteTypes";
import {
  createPlacesSessionToken,
  fetchPlaceDetails,
  fetchPlacesAutocomplete,
  type PlacesSuggestion,
} from "../utils/placesAutocomplete";

type Site = {
  _id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  imageUrl?: string | null;
  imageThumbUrl?: string | null;
  siteType?: string | null;
  status?: string | null;
  borderingRoads?: string | null;
  geoFenceType?: string | null;
  geoFenceCoordinates?: string | null;
  GeoFenceCoordinatesJson?: string | null;
  description?: string | null;
  propertyManagerName?: string | null;
  propertyManagerPhone?: string | null;
  propertyManagerEmail?: string | null;
  businesses?: Array<{ name: string }> | null;
};

type TabKey = "overview" | "contacts" | "tenants";

type SitePatrolAverage = {
  siteId: string;
  siteName: string;
  replayCount: number;
  totalDurationMs: number;
  lastReplayEndTime: number;
};

function splitBusinessAndSuite(rawName: string) {
  const raw = (rawName || "").trim();
  if (!raw) return { businessName: "", suite: null as string | null };

  // Data is commonly stored like: "Business Name (Suite 200)".
  // Only split when "(Suite ...)" is at the very end.
  const m = raw.match(/^(.*)\(\s*(Suite[^)]*)\s*\)\s*$/i);
  if (!m) return { businessName: raw, suite: null };

  const businessName = (m[1] || "").trim();
  const suite = (m[2] || "").trim();
  return { businessName: businessName || raw, suite: suite || null };
}

function normalizeSuiteInput(raw: string) {
  const t = (raw || "").trim();
  if (!t) return "";
  // Allow "200" -> "Suite 200", otherwise keep user's string.
  if (/^\d{1,6}$/.test(t)) return `Suite ${t}`;
  if (/^suite\b/i.test(t)) return t.replace(/^suite\b/i, "Suite");
  return t;
}

function formatTenantSuiteLabel(suite: string | null | undefined): string | null {
  const t = (suite || "").trim();
  if (!t) return null;
  if (/^suite\b/i.test(t)) return t.replace(/^suite\b/i, "Suite");
  return `Suite ${t}`;
}

function formatDuration(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms <= 0) return "-";
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatPatrolTime(ts: number | null | undefined): { display: string; full: string } {
  if (typeof ts !== "number" || !Number.isFinite(ts) || ts <= 0) return { display: "-", full: "-" };
  const d = new Date(ts);
  const time = d.toLocaleString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const date = d.toLocaleString([], { month: "short", day: "numeric" });
  const full = d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
  return { display: `${time}  ${date}`, full };
}

function SdpSection({
  title,
  count,
  children,
  flush,
  className,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <section className={`sdp-sect${className ? ` ${className}` : ""}`}>
      <div className="sdp-block">
        <header className="sdp-block-head">
          <h3 className="sdp-block-title">{title}</h3>
          {typeof count === "number" ? <span className="sdp-block-count">{count}</span> : null}
        </header>
        <div className={`sdp-block-body${flush ? " sdp-block-body--flush" : ""}`}>{children}</div>
      </div>
    </section>
  );
}

function SdpItemList({ items, emptyLabel }: { items: string[]; emptyLabel: string }) {
  if (!items.length) {
    return (
      <div className="sdp-block-pad">
        <div className="sdp-empty sdp-empty--inline">{emptyLabel}</div>
      </div>
    );
  }
  return (
    <ul className="sdp-item-list">
      {items.map((item) => (
        <li key={item} className="sdp-item-row">{item}</li>
      ))}
    </ul>
  );
}

// Component: Shows which tours patrol this site (overview/contacts footer)
function PatrolledBySection({ siteName }: { siteName: string }) {
  const tours = useQuery((api as any).trackTikAnalytics?.getToursForSite, { siteName });
  const tourNames = tours?.map((tour: any) => tour.tourName as string) ?? [];

  return (
    <div className="sdp-patrol-footer" aria-label="Patrolled by">
      <span className="sdp-patrol-footer-label">Patrolled by:</span>
      {!tours ? (
        <span className="sdp-patrol-footer-muted">Loading…</span>
      ) : tourNames.length ? (
        <div className="sdp-chips sdp-chips--inline">
          {tourNames.map((name: string) => (
            <span key={name} className="sdp-chip">{name}</span>
          ))}
        </div>
      ) : (
        <span className="sdp-patrol-footer-muted">No tour patrols this site.</span>
      )}
    </div>
  );
}

async function copyTextToClipboard(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

function SdpContactCopyLine({
  kind,
  value,
  icon: IconComponent,
}: {
  kind: "phone" | "email";
  value: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}) {
  const [copied, setCopied] = useState(false);
  const label = kind === "phone" ? "phone number" : "email address";

  const handleCopy = async () => {
    const ok = await copyTextToClipboard(value);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      className="sdp-contact-line"
      onClick={() => void handleCopy()}
      aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
    >
      <IconComponent className="sdp-contact-line-icon" />
      <span>{value}</span>
    </button>
  );
}

function composeTenantDisplayName(businessName: string, suite: string) {
  const name = (businessName || "").trim();
  const s = normalizeSuiteInput(suite);
  if (!name) return "";
  return s ? `${name} (${s})` : name;
}

const Icon = {
  Phone: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M8 3h3l1 5-2 1c1 3 3 5 6 6l1-2 5 1v3c0 1-1 2-2 2C10 21 3 14 3 5c0-1 1-2 2-2h3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Mail: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M4 6h16v12H4V6Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M4 7l8 6 8-6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Pin: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M12 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Pencil: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M12 20h9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Save: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M17 21v-8H7v8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 3v5h8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  ),
  Back: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

export default function SiteDetailsPanel({
  site,
  onClose,
  isNew,
  onCreated,
}: {
  site: Site;
  onClose: () => void;
  isNew?: boolean;
  onCreated?: (newSiteId: string) => void;
}) {
  const [tab, setTab] = useState<TabKey>("overview");
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);
  const [isHeroImgLoaded, setIsHeroImgLoaded] = useState(false);
  const [isGeneratingThumb, setIsGeneratingThumb] = useState(false);
  const [localThumbUrl, setLocalThumbUrl] = useState<string | null>(null);
  const [isFullscreenFullLoaded, setIsFullscreenFullLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const generateUploadUrl = useMutation(api.sites.generateUploadUrl);
  const saveSiteThumbnail = useMutation(api.sites.saveSiteThumbnail);
  const updateSiteDetails = useMutation(api.sites.updateDetails);
  const saveSiteImage = useMutation(api.sites.saveSiteImage);
  const createSite = useMutation(api.sites.create);
  const processSiteImageUpload = useAction(
    (api as any).siteImageProcessing.processSiteImageUpload,
  );
  const patrolAverageForSite = useQuery((api as any).analytics.getPatrolReplaySiteAverageForSite, {
    siteId: site._id,
  }) as SitePatrolAverage | null | undefined;

  // Prefer thumbnail for speed, but ALWAYS fallback to full image so the UI never "loses" images
  // for existing sites that don't have thumbnails yet.
  const heroImageSrc = localThumbUrl || site.imageThumbUrl || site.imageUrl || null;
  const hasFullImage = !!site.imageUrl;
  const heroIsFullFallback = !!site.imageUrl && !localThumbUrl && !site.imageThumbUrl;
  const heroImageSrcRef = useRef<string | null>(null);
  const isCreateMode = !!isNew;
  const [pendingCreateImageFile, setPendingCreateImageFile] = useState<File | null>(null);
  // After CloudConvert + sharp finishes for a create-mode upload, we hold the
  // processed storage ids here and attach them to the new site on Save.
  const [pendingCreateStorageId, setPendingCreateStorageId] = useState<string | null>(null);
  const [pendingCreateThumbStorageId, setPendingCreateThumbStorageId] = useState<string | null>(null);
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);
  const [photoProcessError, setPhotoProcessError] = useState<string | null>(null);

  const [draftName, setDraftName] = useState<string>(site.name || "");
  const [draftAddress, setDraftAddress] = useState<string>(site.address || "");
  const [draftSiteType, setDraftSiteType] = useState<string>(site.siteType || "");
  const [draftDescription, setDraftDescription] = useState<string>(site.description || "");
  const [draftBorderingRoads, setDraftBorderingRoads] = useState<string>(site.borderingRoads || "");
  const [draftManagerName, setDraftManagerName] = useState<string>(site.propertyManagerName || "");
  const [draftManagerPhone, setDraftManagerPhone] = useState<string>(site.propertyManagerPhone || "");
  const [draftManagerEmail, setDraftManagerEmail] = useState<string>(site.propertyManagerEmail || "");
  const [draftTenants, setDraftTenants] = useState<Array<{ businessName: string; suite: string }>>([]);
  const [tenantBulkText, setTenantBulkText] = useState<string>("");


  // New editor UI state: tenant add-row inputs, dropzone interaction, and roads input.
  const [tenantInputName, setTenantInputName] = useState<string>("");
  const [tenantInputSuite, setTenantInputSuite] = useState<string>("");
  const [roadInputDraft, setRoadInputDraft] = useState<string>("");
  const [editorDragOver, setEditorDragOver] = useState(false);
  const [editorSiteTypeOpen, setEditorSiteTypeOpen] = useState(false);
  const editorPhotoInputRef = useRef<HTMLInputElement | null>(null);
  const editorSiteTypeRef = useRef<HTMLDivElement | null>(null);
  const editorSiteTypeTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [editorTypePanelPos, setEditorTypePanelPos] = useState<{
    direction: "below" | "above";
    maxHeight: number;
  }>({ direction: "below", maxHeight: 280 });
  // Required-field "touched" tracker: a field is added when the user blurs
  // it (or otherwise interacts and moves on) without filling it. The
  // asterisk only renders for fields in this set whose value is still empty.
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Address autocomplete state. Matches the RSPNDR search behavior in the
  // main map sidebar: we fire Google Places Autocomplete once the user has
  // typed at least 3 characters, render suggestions in a dropdown, and on
  // pick we keep the resolved placeId so we can skip the text-search
  // geocoding step on save.
  const [addressSuggestions, setAddressSuggestions] = useState<PlacesSuggestion[]>([]);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const [addressSearchError, setAddressSearchError] = useState<string | null>(null);
  const [addressIsSearching, setAddressIsSearching] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const addressDropdownRef = useRef<HTMLDivElement | null>(null);
  const placesSessionTokenRef = useRef<string | null>(null);
  const addressRequestSeqRef = useRef(0);

  const roadsArray = useMemo(() =>
    (draftBorderingRoads || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  [draftBorderingRoads]);

  const setRoadsArray = (next: string[]) => setDraftBorderingRoads(next.join(", "));

  const hasPhoto = !!(
    localThumbUrl ||
    site.imageThumbUrl ||
    site.imageUrl ||
    pendingCreateImageFile ||
    pendingCreateStorageId
  );

  const requiredChecks = useMemo(() => [
    !!draftName.trim(),
    !!draftAddress.trim(),
    !!draftSiteType.trim(),
    !!draftDescription.trim(),
    roadsArray.length > 0,
    hasPhoto,
    !!draftManagerName.trim(),
    !!draftManagerPhone.trim(),
    !!draftManagerEmail.trim(),
    draftTenants.filter((t) => t.businessName.trim()).length > 0,
  ], [
    draftName,
    draftAddress,
    draftSiteType,
    draftDescription,
    roadsArray.length,
    hasPhoto,
    draftManagerName,
    draftManagerPhone,
    draftManagerEmail,
    draftTenants,
  ]);

  const allRequiredValid = requiredChecks.every(Boolean);

  useEffect(() => {
    heroImageSrcRef.current = heroImageSrc;
    // Prevent showing the previous site's bitmap while the next image loads.
    setIsHeroImgLoaded(false);
  }, [heroImageSrc, site._id]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!hasFullImage) return;
      if (site.imageThumbUrl) return;
      if (isGeneratingThumb) return;

      setIsGeneratingThumb(true);
      try {
        const res = await fetch(site.imageUrl as string, { cache: "force-cache" });
        if (!res.ok) throw new Error(`SITE-THUMB-001 Failed to fetch image: ${res.status}`);
        const blob = await res.blob();

        const maxDim = 640;
        const quality = 0.78;

        const toThumbBlob = async (input: Blob): Promise<Blob> => {
          // Prefer createImageBitmap when available.
          if (typeof createImageBitmap === "function") {
            const bitmap = await createImageBitmap(input);
            const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
            const w = Math.max(1, Math.round(bitmap.width * scale));
            const h = Math.max(1, Math.round(bitmap.height * scale));
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            if (!ctx) throw new Error("SITE-THUMB-002 Canvas ctx missing");
            ctx.drawImage(bitmap, 0, 0, w, h);
            bitmap.close?.();
            const out = await new Promise<Blob | null>((resolve) =>
              canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
            );
            if (!out) throw new Error("SITE-THUMB-003 Failed to encode thumbnail");
            return out;
          }

          // Fallback.
          const img = new Image();
          img.decoding = "async";
          const url = URL.createObjectURL(input);
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("SITE-THUMB-004 Image decode failed"));
            img.src = url;
          });
          const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
          const w = Math.max(1, Math.round(img.naturalWidth * scale));
          const h = Math.max(1, Math.round(img.naturalHeight * scale));
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("SITE-THUMB-005 Canvas ctx missing");
          ctx.drawImage(img, 0, 0, w, h);
          URL.revokeObjectURL(url);
          const out = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
          );
          if (!out) throw new Error("SITE-THUMB-006 Failed to encode thumbnail");
          return out;
        };

        const thumbBlob = await toThumbBlob(blob);
        if (cancelled) return;

        // Immediately show locally (avoid waiting for query refresh).
        try {
          const objUrl = URL.createObjectURL(thumbBlob);
          setLocalThumbUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return objUrl;
          });
        } catch {
          // ignore
        }

        const uploadUrl = await generateUploadUrl();
        const up = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": "image/jpeg" },
          body: thumbBlob,
        });
        if (!up.ok) throw new Error(`SITE-THUMB-007 Upload failed: ${up.status}`);
        const { storageId } = await up.json();
        if (!storageId) throw new Error("SITE-THUMB-008 Missing storageId");

        if (cancelled) return;
        await saveSiteThumbnail({ siteId: site._id as any, thumbStorageId: storageId });
      } catch (e) {
        console.warn("Site thumbnail generation failed:", e);
      } finally {
        if (!cancelled) setIsGeneratingThumb(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
      setLocalThumbUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [generateUploadUrl, hasFullImage, isGeneratingThumb, saveSiteThumbnail, site._id, site.imageThumbUrl, site.imageUrl]);

  useEffect(() => {
    // Reset per-site visual state
    setIsHeroImgLoaded(false);
    setIsFullscreenFullLoaded(false);
    setIsEditing(isCreateMode ? true : false);
    setSaveError(null);
    setDraftName(site.name || "");
    setDraftAddress(site.address || "");
    setDraftSiteType(site.siteType || "");
    setDraftDescription(site.description || "");
    setDraftBorderingRoads(site.borderingRoads || "");
    setDraftManagerName(site.propertyManagerName || "");
    setDraftManagerPhone(site.propertyManagerPhone || "");
    setDraftManagerEmail(site.propertyManagerEmail || "");
    setTenantBulkText("");
    setPendingCreateImageFile(null);
    setPendingCreateStorageId(null);
    setPendingCreateThumbStorageId(null);
    setIsProcessingPhoto(false);
    setPhotoProcessError(null);
    setTenantInputName("");
    setTenantInputSuite("");
    setRoadInputDraft("");
    setEditorDragOver(false);
    setEditorSiteTypeOpen(false);
    setTouchedFields(new Set());
    setAddressSuggestions([]);
    setAddressDropdownOpen(false);
    setAddressSearchError(null);
    setAddressIsSearching(false);
    setSelectedPlaceId(null);
    placesSessionTokenRef.current = null;

    const raw = site.businesses || [];
    const rows = raw
      .map((b) => (b?.name || "").trim())
      .filter(Boolean)
      .map((name) => {
        const s = splitBusinessAndSuite(name);
        return { businessName: s.businessName, suite: s.suite || "" };
      });
    setDraftTenants(rows);
  }, [site._id]);

  const tenants = useMemo(() => {
    const raw = site.businesses || [];
    return raw
      .map((b) => (b?.name || "").trim())
      .filter(Boolean)
      .map((name) => {
        const { businessName, suite } = splitBusinessAndSuite(name);
        return { rawName: name, businessName, suite };
      })
      .sort((a, b) => {
        const byName = a.businessName.localeCompare(b.businessName);
        if (byName !== 0) return byName;
        return (a.suite || "").localeCompare(b.suite || "");
      });
  }, [site.businesses]);

  const tenantsCount = useMemo(() => {
    if (!isEditing) return tenants.length;
    return draftTenants.map((t) => (t.businessName || "").trim()).filter(Boolean).length;
  }, [draftTenants, isEditing, tenants.length]);

  // (coordsText removed from UI, keep available later if needed)

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isImageFullscreen) {
          setIsImageFullscreen(false);
          return;
        }
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isImageFullscreen, onClose]);

  // Click-outside handling for the editor's custom Site type dropdown.
  useEffect(() => {
    if (!editorSiteTypeOpen) return;
    const handleOutside = (e: MouseEvent) => {
      const el = editorSiteTypeRef.current;
      if (el && !el.contains(e.target as Node)) {
        setEditorSiteTypeOpen(false);
        setTouchedFields((prev) => {
          if (prev.has("type")) return prev;
          const next = new Set(prev);
          next.add("type");
          return next;
        });
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [editorSiteTypeOpen]);

  // Compute whether the dropdown should open above or below the trigger so it
  // never overflows the viewport. Runs every time the dropdown opens.
  useEffect(() => {
    if (!editorSiteTypeOpen) return;
    const compute = () => {
      const trigger = editorSiteTypeTriggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const VIEWPORT_BUFFER = 16;
      const GAP = 5;
      const spaceBelow = Math.max(0, viewportHeight - rect.bottom - VIEWPORT_BUFFER - GAP);
      const spaceAbove = Math.max(0, rect.top - VIEWPORT_BUFFER - GAP);
      // Prefer below when there's enough room or when below has more space.
      const preferBelow = spaceBelow >= 220 || spaceBelow >= spaceAbove;
      setEditorTypePanelPos({
        direction: preferBelow ? "below" : "above",
        maxHeight: Math.max(120, preferBelow ? spaceBelow : spaceAbove),
      });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [editorSiteTypeOpen]);

  // Debounced address autocomplete. Only runs while the editor is open and the
  // user has typed at least 3 characters. We bump a sequence id so stale
  // responses are dropped if the user keeps typing.
  useEffect(() => {
    if (!isEditing) return;
    if (!addressDropdownOpen) return;

    const query = draftAddress.trim();
    if (query.length < 3) {
      setAddressSuggestions([]);
      setAddressSearchError(null);
      setAddressIsSearching(false);
      return;
    }

    const requestId = ++addressRequestSeqRef.current;
    setAddressIsSearching(true);
    const handle = window.setTimeout(async () => {
      try {
        if (!placesSessionTokenRef.current) {
          placesSessionTokenRef.current = createPlacesSessionToken();
        }
        const suggestions = await fetchPlacesAutocomplete(
          query,
          placesSessionTokenRef.current,
        );
        if (requestId !== addressRequestSeqRef.current) return;
        setAddressSearchError(null);
        setAddressSuggestions(suggestions.slice(0, 8));
      } catch (e) {
        if (requestId !== addressRequestSeqRef.current) return;
        const msg = e instanceof Error ? e.message : "Failed to load address search";
        setAddressSearchError(msg);
        setAddressSuggestions([]);
      } finally {
        if (requestId === addressRequestSeqRef.current) {
          setAddressIsSearching(false);
        }
      }
    }, 200);

    return () => window.clearTimeout(handle);
  }, [draftAddress, addressDropdownOpen, isEditing]);

  // Click-outside handling for the address autocomplete dropdown.
  useEffect(() => {
    if (!addressDropdownOpen) return;
    const handleOutside = (e: MouseEvent) => {
      const dd = addressDropdownRef.current;
      const inp = addressInputRef.current;
      const target = e.target as Node | null;
      if (target && dd && dd.contains(target)) return;
      if (target && inp && inp.contains(target)) return;
      setAddressDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [addressDropdownOpen]);

  const selectAddressSuggestion = (s: PlacesSuggestion) => {
    setDraftAddress(s.description || s.mainText || "");
    setSelectedPlaceId(s.placeId || null);
    setAddressSuggestions([]);
    setAddressDropdownOpen(false);
    setAddressSearchError(null);
    markTouched("address");
  };

  // Photo file handler for the editor's dropzone. The full pipeline (any input
  // format → JPEG via CloudConvert if needed → resize + compress with sharp on
  // the Convex side, with fallbacks until the file is under 10MB) lives in
  // `convex/siteImageProcessing.ts`. The client just uploads the raw bytes,
  // calls the action, and uses the returned storage ids.
  const processPhotoFile = async (file: File) => {
    setSaveError(null);
    setPhotoProcessError(null);

    // Show the raw file immediately so the user gets an instant preview while
    // the server does its work.
    try {
      const objUrl = URL.createObjectURL(file);
      setLocalThumbUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objUrl;
      });
    } catch {
      // ignore preview failure; the processed image will replace it shortly
    }

    setIsProcessingPhoto(true);
    try {
      const uploadUrl = await generateUploadUrl();
      const uploadResp = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!uploadResp.ok) {
        throw new Error(`SITE-EDT-IMG-001 Raw upload failed: ${uploadResp.status}`);
      }
      const { storageId: rawStorageId } = await uploadResp.json();
      if (!rawStorageId) {
        throw new Error("SITE-EDT-IMG-002 Raw upload missing storageId");
      }

      const result = (await processSiteImageUpload({
        uploadStorageId: rawStorageId,
        mimeType: file.type || undefined,
        fileName: file.name || undefined,
      } as any)) as {
        storageId: string;
        thumbStorageId: string;
      };

      if (!result?.storageId || !result?.thumbStorageId) {
        throw new Error("SITE-EDT-IMG-003 Processed image missing storageId");
      }

      if (isCreateMode) {
        setPendingCreateStorageId(result.storageId);
        setPendingCreateThumbStorageId(result.thumbStorageId);
        setPendingCreateImageFile(file);
      } else {
        await saveSiteImage({
          siteId: site._id as any,
          storageId: result.storageId as any,
          thumbStorageId: result.thumbStorageId as any,
        });
        setIsHeroImgLoaded(false);
      }
    } catch (err: any) {
      setPhotoProcessError(
        String(err?.message || err || "SITE-EDT-IMG-007 Photo processing failed"),
      );
      // Roll back the preview so users don't think it succeeded.
      setLocalThumbUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPendingCreateImageFile(null);
      setPendingCreateStorageId(null);
      setPendingCreateThumbStorageId(null);
    } finally {
      setIsProcessingPhoto(false);
    }
  };

  const removeStagedPhoto = () => {
    setPendingCreateImageFile(null);
    setPendingCreateStorageId(null);
    setPendingCreateThumbStorageId(null);
    setPhotoProcessError(null);
    setLocalThumbUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const addRoadTag = (raw: string) => {
    const v = raw.trim().replace(/,+$/g, "").trim();
    if (!v) return;
    if (roadsArray.includes(v)) {
      setRoadInputDraft("");
      return;
    }
    setRoadsArray([...roadsArray, v]);
    setRoadInputDraft("");
  };

  const removeRoadTag = (idx: number) => {
    const next = roadsArray.filter((_, i) => i !== idx);
    setRoadsArray(next);
  };

  const addTenantFromInputs = () => {
    const name = tenantInputName.trim();
    if (!name) return;
    setDraftTenants((prev) => [...prev, { businessName: name, suite: tenantInputSuite.trim() }]);
    setTenantInputName("");
    setTenantInputSuite("");
  };

  const removeTenantAt = (idx: number) => {
    setDraftTenants((prev) => prev.filter((_, i) => i !== idx));
  };

  const markTouched = (field: string) => {
    setTouchedFields((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };
  const showReq = (field: string, isFilled: boolean) =>
    touchedFields.has(field) && !isFilled;

  const applyBulkTenants = () => {
    const lines = tenantBulkText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return;
    const parsed = lines.map((name) => {
      const s = splitBusinessAndSuite(name);
      return { businessName: s.businessName, suite: s.suite || "" };
    });
    setDraftTenants((prev) => [...prev, ...parsed]);
    setTenantBulkText("");
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      const businesses = draftTenants
        .map((t) => composeTenantDisplayName(t.businessName, t.suite))
        .map((name) => name.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ name }));

      if (isCreateMode) {
        let lat: number | null = null;
        let lng: number | null = null;
        let resolvedAddress = draftAddress;

        // Fast path: if the user picked an autocomplete suggestion we already
        // know the placeId, so we can resolve coordinates via Place Details
        // and skip the textSearch round-trip.
        if (selectedPlaceId) {
          try {
            const details = await fetchPlaceDetails(
              selectedPlaceId,
              placesSessionTokenRef.current,
            );
            lat = details.lat;
            lng = details.lng;
            if (details.address) resolvedAddress = details.address;
            placesSessionTokenRef.current = null;
          } catch {
            // fall through to textSearch
          }
        }

        if (lat == null || lng == null) {
          const GOOGLE_MAPS_API_KEY = (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY as
            | string
            | undefined;
          if (!GOOGLE_MAPS_API_KEY) {
            throw new Error("SITE-CRT-UI-001 Missing VITE_GOOGLE_MAPS_API_KEY");
          }

          const geoRes = await fetch("https://places.googleapis.com/v1/places:searchText", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": GOOGLE_MAPS_API_KEY,
              "X-Goog-FieldMask": "places.location,places.formattedAddress",
            },
            body: JSON.stringify({ textQuery: draftAddress, maxResultCount: 1 }),
          });
          if (!geoRes.ok) {
            throw new Error(`SITE-CRT-UI-002 Geocode request failed: ${geoRes.status}`);
          }
          const geoJson = await geoRes.json();
          const loc = geoJson?.places?.[0]?.location;
          const geoLat = loc?.latitude;
          const geoLng = loc?.longitude;
          if (typeof geoLat !== "number" || typeof geoLng !== "number") {
            throw new Error("SITE-CRT-UI-003 Geocode failed: no location match");
          }
          lat = geoLat;
          lng = geoLng;
        }

        const created = await createSite({
          name: draftName,
          address: resolvedAddress,
          latitude: lat,
          longitude: lng,
          description: draftDescription,
          borderingRoads: draftBorderingRoads,
          propertyManagerName: draftManagerName,
          propertyManagerPhone: draftManagerPhone,
          propertyManagerEmail: draftManagerEmail,
          businesses,
          siteType: draftSiteType,
        } as any);

        const newSiteId = (created as any)?.siteId as string | undefined;
        if (!newSiteId) throw new Error("SITE-CRT-UI-005 Create did not return siteId");

        // The photo was already converted + compressed when it was dropped
        // into the dropzone — we just attach the resulting storage ids to
        // the new site.
        if (pendingCreateStorageId && pendingCreateThumbStorageId) {
          await saveSiteImage({
            siteId: newSiteId as any,
            storageId: pendingCreateStorageId as any,
            thumbStorageId: pendingCreateThumbStorageId as any,
          } as any);
        }

        onCreated?.(newSiteId);
        setIsEditing(false);
      } else {
        await updateSiteDetails({
          siteId: site._id as any,
          name: draftName,
          address: draftAddress,
          description: draftDescription,
          borderingRoads: draftBorderingRoads,
          propertyManagerName: draftManagerName,
          propertyManagerPhone: draftManagerPhone,
          propertyManagerEmail: draftManagerEmail,
          businesses,
          siteType: draftSiteType,
        });
        setIsEditing(false);
      }
    } catch (e: any) {
      const msg = String(e?.message || e || "SITE-UPD-UI-001 Save failed");
      setSaveError(msg);
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      onClose();
      return;
    }
    setIsEditing(false);
    setSaveError(null);
    setDraftName(site.name || "");
    setDraftAddress(site.address || "");
    setDraftSiteType(site.siteType || "");
    setDraftDescription(site.description || "");
    setDraftBorderingRoads(site.borderingRoads || "");
    setDraftManagerName(site.propertyManagerName || "");
    setDraftManagerPhone(site.propertyManagerPhone || "");
    setDraftManagerEmail(site.propertyManagerEmail || "");
    setPendingCreateImageFile(null);
    setPendingCreateStorageId(null);
    setPendingCreateThumbStorageId(null);
    setIsProcessingPhoto(false);
    setPhotoProcessError(null);
    setTenantInputName("");
    setTenantInputSuite("");
    setRoadInputDraft("");
    setEditorDragOver(false);
    setEditorSiteTypeOpen(false);
    setTouchedFields(new Set());
    const raw = site.businesses || [];
    setDraftTenants(
      raw
        .map((b) => (b?.name || "").trim())
        .filter(Boolean)
        .map((name) => {
          const s = splitBusinessAndSuite(name);
          return { businessName: s.businessName, suite: s.suite || "" };
        })
    );
    setTenantBulkText("");
  };

  if (isEditing) {
    const editorSiteTypes = ["Commercial", "Residential", "Industrial", "Retail", "Mixed use", ...SITE_TYPES.filter(
      (t) => !["Commercial", "Residential", "Industrial", "Retail", "Mixed use"].includes(t)
    )];
    const editorTabIndex = tab === "overview" ? 0 : tab === "contacts" ? 1 : 2;
    const tenantsCountForBadge = draftTenants.map((t) => t.businessName.trim()).filter(Boolean).length;

    return (
      <aside
        className={`site-editor-panel ${isCreateMode ? "site-editor-panel--create" : "site-editor-panel--edit"}`}
        aria-label={isCreateMode ? "Create new site" : "Edit site"}
      >
        <input
          ref={editorPhotoInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            await processPhotoFile(file);
            if (editorPhotoInputRef.current) editorPhotoInputRef.current.value = "";
          }}
        />

        <header className="site-editor-header">
          <div className="site-editor-header-left">
            <button
              type="button"
              className="site-editor-back"
              onClick={onClose}
              aria-label="Back to sites"
              title="Back to sites"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="site-editor-header-titles">
              <div className="site-editor-title">
                {isCreateMode ? "New site" : (draftName.trim() || site.name || "Edit site")}
              </div>
            </div>
          </div>
          <div className="site-editor-header-right">
            <button
              type="button"
              className="site-editor-btn site-editor-btn-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="site-editor-btn site-editor-btn-primary"
              disabled={!allRequiredValid || isProcessingPhoto}
              onClick={handleSave}
              title={isProcessingPhoto ? "Photo still processing…" : (allRequiredValid ? "Save" : "Complete all required fields")}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <path d="M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
              Save
            </button>
          </div>
        </header>

        <div className="site-editor-segmented" role="tablist" aria-label="Editor sections">
          <div className="site-editor-segmented-indicator" data-pos={editorTabIndex} aria-hidden="true" />
          <button
            type="button"
            role="tab"
            aria-selected={tab === "overview"}
            className={`site-editor-seg-btn ${tab === "overview" ? "active" : ""}`}
            onClick={() => setTab("overview")}
          >
            OVERVIEW
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "contacts"}
            className={`site-editor-seg-btn ${tab === "contacts" ? "active" : ""}`}
            onClick={() => setTab("contacts")}
          >
            CONTACTS
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "tenants"}
            className={`site-editor-seg-btn site-editor-seg-btn--tenants ${tab === "tenants" ? "active" : ""}`}
            onClick={() => setTab("tenants")}
          >
            TENANTS
            <span
              className={`site-editor-seg-badge ${tenantsCountForBadge > 0 ? "active" : ""}`}
              aria-label={`${tenantsCountForBadge} tenants`}
            >
              {tenantsCountForBadge}
            </span>
          </button>
        </div>

        {saveError ? <div className="site-editor-error">{saveError}</div> : null}

        {tab === "overview" ? (
          <div className="site-editor-tab-panel">
            <div className="site-editor-group-label">Identity</div>

            <div className="site-editor-field">
              <label className="site-editor-field-label" htmlFor="site-editor-name">
                Site name{showReq("name", !!draftName.trim()) ? <span className="site-editor-req">*</span> : null}
              </label>
              <input
                id="site-editor-name"
                type="text"
                className="site-editor-input"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onBlur={() => markTouched("name")}
                placeholder="e.g. Wood Colony"
              />
            </div>

            <div className="site-editor-field site-editor-field--address">
              <label className="site-editor-field-label" htmlFor="site-editor-address">
                Site address{showReq("address", !!draftAddress.trim()) ? <span className="site-editor-req">*</span> : null}
              </label>
              <input
                ref={addressInputRef}
                id="site-editor-address"
                type="text"
                className="site-editor-input"
                value={draftAddress}
                onChange={(e) => {
                  setDraftAddress(e.target.value);
                  setSelectedPlaceId(null);
                  setAddressDropdownOpen(true);
                }}
                onFocus={() => {
                  if (draftAddress.trim().length >= 3) {
                    setAddressDropdownOpen(true);
                  }
                }}
                onBlur={() => markTouched("address")}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setAddressDropdownOpen(false);
                  }
                }}
                placeholder="Start typing an address…"
                autoComplete="off"
                spellCheck={false}
              />
              {addressDropdownOpen && draftAddress.trim().length >= 3 ? (
                <div
                  className="site-editor-address-dropdown"
                  ref={addressDropdownRef}
                  role="listbox"
                  aria-label="Address suggestions"
                >
                  {addressSearchError ? (
                    <div className="site-editor-address-dropdown-empty">
                      {addressSearchError}
                    </div>
                  ) : addressSuggestions.length ? (
                    addressSuggestions.map((p) => (
                      <button
                        key={p.placeId || p.description}
                        type="button"
                        className="site-editor-address-dropdown-item"
                        role="option"
                        aria-selected={selectedPlaceId === p.placeId}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectAddressSuggestion(p)}
                      >
                        <div className="site-editor-address-dropdown-name">
                          {p.mainText || p.description}
                        </div>
                        {p.secondaryText ? (
                          <div className="site-editor-address-dropdown-sub">
                            {p.secondaryText}
                          </div>
                        ) : null}
                      </button>
                    ))
                  ) : addressIsSearching ? (
                    <div className="site-editor-address-dropdown-empty">
                      Searching…
                    </div>
                  ) : (
                    <div className="site-editor-address-dropdown-empty">
                      No matching addresses
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="site-editor-field">
              <label className="site-editor-field-label" id="site-editor-type-label">
                Site type{showReq("type", !!draftSiteType.trim()) ? <span className="site-editor-req">*</span> : null}
              </label>
              <div className="ec-dropdown site-editor-type-dropdown" ref={editorSiteTypeRef}>
                <button
                  ref={editorSiteTypeTriggerRef}
                  type="button"
                  className={`ec-dropdown-trigger${editorSiteTypeOpen ? " ec-dropdown-trigger--open" : ""}`}
                  onClick={() => {
                    setEditorSiteTypeOpen((o) => {
                      const next = !o;
                      if (!next) markTouched("type");
                      return next;
                    });
                  }}
                  aria-haspopup="listbox"
                  aria-expanded={editorSiteTypeOpen}
                  aria-labelledby="site-editor-type-label"
                >
                  <span
                    className="ec-dropdown-trigger-label"
                    style={!draftSiteType ? { color: "var(--ui-text-muted)" } : undefined}
                  >
                    {draftSiteType || "Select a type…"}
                  </span>
                  <svg
                    className="ec-dropdown-chevron"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.5 4.5L6 8L9.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {editorSiteTypeOpen ? (
                  <ul
                    className={`ec-dropdown-panel ec-dropdown-panel--${editorTypePanelPos.direction}`}
                    role="listbox"
                    aria-labelledby="site-editor-type-label"
                    style={{
                      maxHeight: editorTypePanelPos.maxHeight,
                      overflowY: "auto",
                    }}
                  >
                    {editorSiteTypes.map((t) => (
                      <li
                        key={t}
                        role="option"
                        aria-selected={t === draftSiteType}
                        className={`ec-dropdown-option${t === draftSiteType ? " ec-dropdown-option--active" : ""}`}
                        onClick={() => {
                          setDraftSiteType(t);
                          markTouched("type");
                          setEditorSiteTypeOpen(false);
                        }}
                      >
                        <span className="ec-dropdown-option-indicator" aria-hidden="true" />
                        {t}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>

            <div className="site-editor-group-label site-editor-group-label--spaced">
              Photos{showReq("photo", hasPhoto) ? <span className="site-editor-req">*</span> : null}
            </div>

            <div
              className={`site-editor-dropzone ${editorDragOver ? "drag-over" : ""} ${hasPhoto ? "has-photo" : ""} ${isProcessingPhoto ? "is-processing" : ""}`}
              onClick={(e) => {
                if (isProcessingPhoto) return;
                if ((e.target as HTMLElement).closest("button")) return;
                editorPhotoInputRef.current?.click();
              }}
              onDragOver={(e) => {
                if (isProcessingPhoto) return;
                e.preventDefault();
                setEditorDragOver(true);
              }}
              onDragEnter={(e) => {
                if (isProcessingPhoto) return;
                e.preventDefault();
                setEditorDragOver(true);
              }}
              onDragLeave={() => setEditorDragOver(false)}
              onDrop={async (e) => {
                if (isProcessingPhoto) return;
                e.preventDefault();
                setEditorDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) await processPhotoFile(file);
              }}
              role="button"
              tabIndex={0}
              aria-label={hasPhoto ? "Replace photo" : "Upload photo"}
              aria-busy={isProcessingPhoto}
              onBlur={() => markTouched("photo")}
              onKeyDown={(e) => {
                if (isProcessingPhoto) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  editorPhotoInputRef.current?.click();
                }
              }}
            >
              {!hasPhoto ? (
                <div className="site-editor-dropzone-empty">
                  <div className="site-editor-dropzone-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 16V4M12 4l-4 4M12 4l4 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="site-editor-dropzone-title">Upload a photo</div>
                  <div className="site-editor-dropzone-hint">Any image format · auto-compressed</div>
                </div>
              ) : (
                <div className="site-editor-dropzone-filled">
                  {(localThumbUrl || site.imageThumbUrl || site.imageUrl) ? (
                    <img
                      src={(localThumbUrl || site.imageThumbUrl || site.imageUrl) as string}
                      alt="Site"
                      className="site-editor-dropzone-preview"
                      loading="eager"
                      decoding="async"
                    />
                  ) : (
                    <div className="site-editor-dropzone-preview site-editor-dropzone-preview--placeholder">
                      Photo
                    </div>
                  )}
                  <div className="site-editor-dropzone-filled-actions">
                    <button
                      type="button"
                      className="site-editor-dropzone-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        editorPhotoInputRef.current?.click();
                      }}
                      disabled={isProcessingPhoto}
                    >
                      Replace
                    </button>
                    {isCreateMode ? (
                      <button
                        type="button"
                        className="site-editor-dropzone-link site-editor-dropzone-link--danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeStagedPhoto();
                        }}
                        disabled={isProcessingPhoto}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
              {isProcessingPhoto ? (
                <div className="site-editor-dropzone-processing" aria-live="polite">
                  Processing image…
                </div>
              ) : null}
            </div>
            {photoProcessError ? (
              <div className="site-editor-field-hint site-editor-field-hint--error">
                {photoProcessError}
              </div>
            ) : null}

            <div className="site-editor-group-label site-editor-group-label--spaced">About</div>

            <div className="site-editor-field">
              <label className="site-editor-field-label" htmlFor="site-editor-desc">
                Description{showReq("description", !!draftDescription.trim()) ? <span className="site-editor-req">*</span> : null}
              </label>
              <textarea
                id="site-editor-desc"
                className="site-editor-input site-editor-textarea"
                value={draftDescription}
                onChange={(e) => setDraftDescription(e.target.value.slice(0, 500))}
                onBlur={() => markTouched("description")}
                placeholder="Access notes, hazards, key details…"
                rows={3}
                maxLength={500}
              />
              <div className="site-editor-field-hint">{draftDescription.length}/500</div>
            </div>

            <div className="site-editor-field">
              <label className="site-editor-field-label">
                Bordering roads{showReq("roads", roadsArray.length > 0) ? <span className="site-editor-req">*</span> : null}
              </label>
              <div
                className="site-editor-tag-input"
                onClick={() => {
                  const el = document.getElementById("site-editor-roads-input") as HTMLInputElement | null;
                  el?.focus();
                }}
              >
                {roadsArray.map((r, i) => (
                  <span key={`${r}-${i}`} className="site-editor-tag">
                    <span>{r}</span>
                    <button
                      type="button"
                      className="site-editor-tag-x"
                      aria-label={`Remove ${r}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRoadTag(i);
                      }}
                    >
                      <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                        <path d="M2 2 L8 8 M8 2 L2 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                      </svg>
                    </button>
                  </span>
                ))}
                <input
                  id="site-editor-roads-input"
                  type="text"
                  className="site-editor-tag-input-field"
                  value={roadInputDraft}
                  placeholder={roadsArray.length === 0 ? "Type and press Enter…" : ""}
                  onChange={(e) => setRoadInputDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addRoadTag(roadInputDraft);
                    } else if (e.key === "Backspace" && !roadInputDraft && roadsArray.length > 0) {
                      removeRoadTag(roadsArray.length - 1);
                    }
                  }}
                  onBlur={() => {
                    if (roadInputDraft.trim()) addRoadTag(roadInputDraft);
                    markTouched("roads");
                  }}
                />
              </div>
            </div>
          </div>
        ) : null}

        {tab === "contacts" ? (
          <div className="site-editor-tab-panel">
            <div className="site-editor-group-label">Property manager</div>

            <div className="site-editor-field">
              <label className="site-editor-field-label" htmlFor="site-editor-pm-name">
                Name{showReq("pm_name", !!draftManagerName.trim()) ? <span className="site-editor-req">*</span> : null}
              </label>
              <input
                id="site-editor-pm-name"
                type="text"
                className="site-editor-input"
                value={draftManagerName}
                onChange={(e) => setDraftManagerName(e.target.value)}
                onBlur={() => markTouched("pm_name")}
                placeholder="Full name"
              />
            </div>

            <div className="site-editor-field-grid-2">
              <div className="site-editor-field">
                <label className="site-editor-field-label" htmlFor="site-editor-pm-phone">
                  Phone{showReq("pm_phone", !!draftManagerPhone.trim()) ? <span className="site-editor-req">*</span> : null}
                </label>
                <input
                  id="site-editor-pm-phone"
                  type="tel"
                  className="site-editor-input"
                  value={draftManagerPhone}
                  onChange={(e) => setDraftManagerPhone(e.target.value)}
                  onBlur={() => markTouched("pm_phone")}
                  placeholder="(555) 555-5555"
                />
              </div>
              <div className="site-editor-field">
                <label className="site-editor-field-label" htmlFor="site-editor-pm-email">
                  Email{showReq("pm_email", !!draftManagerEmail.trim()) ? <span className="site-editor-req">*</span> : null}
                </label>
                <input
                  id="site-editor-pm-email"
                  type="email"
                  className="site-editor-input"
                  value={draftManagerEmail}
                  onChange={(e) => setDraftManagerEmail(e.target.value)}
                  onBlur={() => markTouched("pm_email")}
                  placeholder="name@example.com"
                />
              </div>
            </div>
          </div>
        ) : null}

        {tab === "tenants" ? (
          <div className="site-editor-tab-panel">
            <div className="site-editor-tenants-head">
              <div className="site-editor-group-label" style={{ marginBottom: 0 }}>
                Tenants{showReq("tenants", draftTenants.some((t) => t.businessName.trim().length > 0)) ? <span className="site-editor-req">*</span> : null}
              </div>
              <span className="site-editor-required-tag">1+ REQUIRED</span>
            </div>

            <div className="site-editor-tenant-add">
              <input
                type="text"
                className="site-editor-input"
                value={tenantInputName}
                onChange={(e) => setTenantInputName(e.target.value)}
                onBlur={() => markTouched("tenants")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTenantFromInputs();
                  }
                }}
                placeholder="Tenant name"
              />
              <input
                type="text"
                className="site-editor-input"
                value={tenantInputSuite}
                onChange={(e) => setTenantInputSuite(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTenantFromInputs();
                  }
                }}
                placeholder="Suite"
              />
              <button
                type="button"
                className="site-editor-tenant-add-btn"
                onClick={addTenantFromInputs}
                disabled={!tenantInputName.trim()}
                aria-label="Add tenant"
                title="Add tenant"
              >
                +
              </button>
            </div>

            <div className="site-editor-tenants-list">
              {draftTenants.length === 0 ? (
                <div className="site-editor-tenants-empty">No tenants added yet</div>
              ) : (
                draftTenants.map((t, i) => (
                  <div className="site-editor-tenant-item" key={`${t.businessName}-${i}`}>
                    <span className="site-editor-tenant-item-text">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M3 10h18l-1.5-5h-15L3 10Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                        <path d="M5 10v10h14V10M10 20v-5h4v5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                      </svg>
                      <span>{t.businessName}</span>
                      {t.suite ? <span className="site-editor-tenant-item-suite"> · {t.suite}</span> : null}
                    </span>
                    <button
                      type="button"
                      className="site-editor-tenant-remove"
                      onClick={() => removeTenantAt(i)}
                      aria-label={`Remove ${t.businessName}`}
                      title="Remove tenant"
                    >
                      <svg width="11" height="11" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                        <path d="M2 2 L8 8 M8 2 L2 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>

            <details className="site-editor-bulk">
              <summary>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="8" y="3" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="1.8" />
                  <rect x="5" y="5" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                </svg>
                Bulk paste
              </summary>
              <textarea
                className="site-editor-input site-editor-textarea"
                value={tenantBulkText}
                onChange={(e) => setTenantBulkText(e.target.value)}
                placeholder={"Starbucks (Suite 120)\nABC Dental (Suite 200)"}
                rows={3}
              />
              <div className="site-editor-bulk-actions">
                <button
                  type="button"
                  className="site-editor-btn site-editor-btn-secondary site-editor-bulk-apply"
                  onClick={applyBulkTenants}
                  disabled={!tenantBulkText.trim()}
                >
                  Add from paste
                </button>
              </div>
            </details>
          </div>
        ) : null}
      </aside>
    );
  }

  return (
    <aside className="sdp" aria-label="Site details">
      {isImageFullscreen && site.imageUrl
        ? createPortal(
            <div
              className="site-image-fullscreen"
              role="dialog"
              aria-label="Site image full screen"
              onClick={() => setIsImageFullscreen(false)}
            >
              {heroImageSrc ? (
                <img
                  className="site-image-fullscreen-img site-image-fullscreen-img-thumb"
                  src={heroImageSrc}
                  alt={site.name}
                  decoding="async"
                  loading="eager"
                  style={{ opacity: isFullscreenFullLoaded ? 0 : 1 }}
                />
              ) : null}
              <img
                className="site-image-fullscreen-img"
                src={site.imageUrl}
                alt={site.name}
                decoding="async"
                loading="eager"
                fetchPriority="high"
                onLoad={() => setIsFullscreenFullLoaded(true)}
                style={{ opacity: isFullscreenFullLoaded ? 1 : 0 }}
              />
              <div className="site-image-fullscreen-hint">Click to close</div>
            </div>,
            document.body
          )
        : null}

      <header className="sdp-top">
        <div className="sdp-topbar">
          <div className="sdp-topbar-copy">
            <h2 className="sdp-top-name" title={site.name}>{site.name}</h2>
            <div className="sdp-top-address" title={site.address}>{site.address}</div>
          </div>
          <button
            type="button"
            className="sdp-topbar-edit"
            onClick={() => {
              setIsEditing(true);
              setSaveError(null);
            }}
            aria-label="Edit site details"
            title="Edit site"
          >
            <Icon.Pencil className="sdp-topbar-edit-icon" />
            Edit
          </button>
        </div>

        <div className="sdp-top-media">
          {heroImageSrc ? (
            <button
              type="button"
              className="sdp-top-media-frame"
              onClick={() => setIsImageFullscreen(true)}
              aria-label="Open site image full screen"
              title="Open image"
            >
              {!isHeroImgLoaded ? <div className="sdp-top-media-loading" aria-hidden="true" /> : null}
              <img
                key={`${site._id}:${heroImageSrc}`}
                className="sdp-top-media-img"
                src={heroImageSrc}
                alt={site.name}
                loading={heroIsFullFallback ? "lazy" : "eager"}
                decoding="async"
                fetchPriority={heroIsFullFallback ? "low" : "high"}
                onLoad={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  const loaded = img.currentSrc || img.src;
                  const expected = heroImageSrcRef.current
                    ? new URL(heroImageSrcRef.current, window.location.href).href
                    : null;
                  if (expected && loaded === expected) setIsHeroImgLoaded(true);
                }}
                style={{ opacity: isHeroImgLoaded ? 1 : 0 }}
              />
            </button>
          ) : (
            <div className="sdp-top-media-frame sdp-top-media-frame--empty" aria-hidden="true">
              <Icon.Pin className="sdp-top-media-placeholder-icon" />
              <span className="sdp-top-media-placeholder-label">No site photo</span>
            </div>
          )}
        </div>
      </header>

      <div className="site-editor-segmented sdp-tabs" role="tablist" aria-label="Site details tabs">
        <div
          className="site-editor-segmented-indicator"
          data-pos={tab === "overview" ? 0 : tab === "contacts" ? 1 : 2}
          aria-hidden="true"
        />
        <button
          type="button"
          role="tab"
          aria-selected={tab === "overview"}
          className={`site-editor-seg-btn ${tab === "overview" ? "active" : ""}`}
          onClick={() => setTab("overview")}
        >
          OVERVIEW
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "contacts"}
          className={`site-editor-seg-btn ${tab === "contacts" ? "active" : ""}`}
          onClick={() => setTab("contacts")}
        >
          CONTACTS
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "tenants"}
          className={`site-editor-seg-btn site-editor-seg-btn--tenants ${tab === "tenants" ? "active" : ""}`}
          onClick={() => setTab("tenants")}
        >
          TENANTS
          <span
            className={`site-editor-seg-badge ${tenantsCount > 0 ? "active" : ""}`}
            aria-label={`${tenantsCount} tenants`}
          >
            {tenantsCount}
          </span>
        </button>
      </div>

      {tab === "tenants" ? (
      <div className="sdp-body sdp-body--tenants" role="tabpanel">
        <section className="sdp-tenants-view" aria-label="Tenants">
          <div className="sdp-tenant-board">
            <header className="sdp-tenants-view-head">
              <span className="sdp-tenants-view-pipe" aria-hidden="true" />
              <h3 className="sdp-tenants-view-title">Tenants</h3>
            </header>

            {tenants.length ? (
              <ul className="sdp-tenants" aria-label="Tenant businesses">
                {tenants.map((t, i) => {
                  const suiteLabel = formatTenantSuiteLabel(t.suite);
                  return (
                    <li key={`${t.rawName}-${i}`} className="sdp-tenant" title={t.rawName}>
                      <span className="sdp-tenant-idx">{String(i + 1).padStart(2, "0")}</span>
                      <span className="sdp-tenant-name">{t.businessName}</span>
                      {suiteLabel ? <span className="sdp-tenant-suite">{suiteLabel}</span> : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="sdp-tenant-board-empty">No tenants listed.</div>
            )}
          </div>
        </section>
      </div>
      ) : (
        <>
          <div className="sdp-overview">
            <div
              className={`sdp-body${tab === "overview" ? " sdp-body--overview" : " sdp-body--contacts"}`}
              role="tabpanel"
            >
              {tab === "overview" ? (
                <>
                  <SdpSection title="About">
                    {site.description ? (
                      <p className="sdp-prose">{site.description}</p>
                    ) : (
                      <div className="sdp-empty sdp-empty--inline">No description provided.</div>
                    )}
                  </SdpSection>

                  <SdpSection title="Patrol activity" flush>
                    {patrolAverageForSite === undefined ? (
                      <div className="sdp-block-pad">
                        <div className="sdp-empty sdp-empty--inline">Loading…</div>
                      </div>
                    ) : patrolAverageForSite && patrolAverageForSite.replayCount > 0 ? (
                      <div className="sdp-statline">
                        <div className="sdp-statline-item">
                          <div className="sdp-statline-label">Avg duration</div>
                          <div className="sdp-statline-value">
                            {formatDuration(patrolAverageForSite.totalDurationMs / patrolAverageForSite.replayCount)}
                          </div>
                        </div>
                        <div className="sdp-statline-item">
                          <div className="sdp-statline-label">Patrols</div>
                          <div className="sdp-statline-value">
                            {patrolAverageForSite.replayCount.toLocaleString()}
                          </div>
                        </div>
                        <div className="sdp-statline-item">
                          <div className="sdp-statline-label">Last patrol</div>
                          <div
                            className="sdp-statline-value"
                            title={formatPatrolTime(patrolAverageForSite.lastReplayEndTime).full}
                          >
                            {formatPatrolTime(patrolAverageForSite.lastReplayEndTime).display}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="sdp-block-pad">
                        <div className="sdp-empty sdp-empty--inline">No patrol data yet.</div>
                      </div>
                    )}
                  </SdpSection>

                  <SdpSection title="Bordering roads" flush>
                    <SdpItemList items={roadsArray} emptyLabel="None listed." />
                  </SdpSection>
                </>
              ) : (
                <SdpSection title="Property manager">
                  {site.propertyManagerName || site.propertyManagerPhone || site.propertyManagerEmail ? (
                    <>
                      <div className="sdp-contact-head">
                        <div className="sdp-avatar" aria-hidden="true">
                          {(site.propertyManagerName || "PM")
                            .split(" ")
                            .slice(0, 2)
                            .map((w: string) => w[0] || "")
                            .join("")
                            .toUpperCase()}
                        </div>
                        <div className="sdp-contact-meta">
                          <div className="sdp-contact-name">{site.propertyManagerName || "Property manager"}</div>
                          <div className="sdp-contact-role">Property manager</div>
                        </div>
                      </div>
                      {(site.propertyManagerPhone || site.propertyManagerEmail) ? (
                        <div className="sdp-contact-lines">
                          {site.propertyManagerPhone ? (
                            <SdpContactCopyLine kind="phone" value={site.propertyManagerPhone} icon={Icon.Phone} />
                          ) : null}
                          {site.propertyManagerEmail ? (
                            <SdpContactCopyLine kind="email" value={site.propertyManagerEmail} icon={Icon.Mail} />
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="sdp-empty sdp-empty--inline">No contact details on file.</div>
                  )}
                </SdpSection>
              )}
            </div>
          </div>

          <footer className="sdp-footer">
            <PatrolledBySection siteName={site.name} />
          </footer>
        </>
      )}
    </aside>
  );
}
